// Angular import
import { Component, OnInit, inject, output, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule, Location, LocationStrategy } from '@angular/common';
import { RouterModule } from '@angular/router';

// project import
import { NavigationItem, NavigationItems } from '../navigation';
import { environment } from 'src/environments/environment';
import { RoleService } from '../../../../../Shared/Services/role.service';

import { NavGroupComponent } from './nav-group/nav-group.component';

// icon
import { IconService } from '@ant-design/icons-angular';
import {
  DashboardOutline,
  CreditCardOutline,
  LoginOutline,
  QuestionOutline,
  ChromeOutline,
  FontSizeOutline,
  ProfileOutline,
  BgColorsOutline,
  AntDesignOutline
} from '@ant-design/icons-angular/icons';
import { NgScrollbarModule } from 'ngx-scrollbar';

@Component({
  standalone: true,
  selector: 'app-nav-content',
  imports: [CommonModule, RouterModule, NavGroupComponent, NgScrollbarModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './nav-content.component.html',
  styleUrls: ['./nav-content.component.scss']
})
export class NavContentComponent implements OnInit {
  private location = inject(Location);
  private locationStrategy = inject(LocationStrategy);
  private iconService = inject(IconService);
  private roleService = inject(RoleService);

  // public props
  NavCollapsedMob = output();

  navigations: NavigationItem[];

  // version
  title = 'Demo application for version numbering';
  currentApplicationVersion = environment.appVersion;

  navigation = NavigationItems;
  windowWidth = window.innerWidth;

  // Constructor
  constructor() {
    this.iconService.addIcon(
      ...[
        DashboardOutline,
        CreditCardOutline,
        FontSizeOutline,
        LoginOutline,
        ProfileOutline,
        BgColorsOutline,
        AntDesignOutline,
        ChromeOutline,
        QuestionOutline
      ]
    );
    this.filterNavigationByRole();
    
    // Subscribe to role changes
    this.roleService.currentRole$.subscribe(() => {
      this.filterNavigationByRole();
    });
  }

  // Life cycle events
  ngOnInit() {
    if (this.windowWidth < 1025) {
      (document.querySelector('.coded-navbar') as HTMLDivElement).classList.add('menupos-static');
    }
  }

  fireOutClick() {
    let current_url = this.location.path();
    const baseHref = this.locationStrategy.getBaseHref();
    if (baseHref) {
      current_url = baseHref + this.location.path();
    }
    const link = "a.nav-link[ href='" + current_url + "' ]";
    const ele = document.querySelector(link);
    if (ele !== null && ele !== undefined) {
      const parent = ele.parentElement;
      const up_parent = parent?.parentElement?.parentElement;
      const last_parent = up_parent?.parentElement;
      if (parent?.classList.contains('coded-hasmenu')) {
        parent.classList.add('coded-trigger');
        parent.classList.add('active');
      } else if (up_parent?.classList.contains('coded-hasmenu')) {
        up_parent.classList.add('coded-trigger');
        up_parent.classList.add('active');
      } else if (last_parent?.classList.contains('coded-hasmenu')) {
        last_parent.classList.add('coded-trigger');
        last_parent.classList.add('active');
      }
    }
  }

  navMob() {
    if (this.windowWidth < 1025 && document.querySelector('app-navigation.coded-navbar').classList.contains('mob-open')) {
      this.NavCollapsedMob.emit();
    }
  }

  private filterNavigationByRole() {
    const currentRole = this.roleService.getCurrentRole();
    if (!currentRole) {
      this.navigations = [];
      return;
    }

    // Admin has access to everything
    if (currentRole.permissions.includes('*')) {
      this.navigations = NavigationItems;
      return;
    }

    this.navigations = NavigationItems.filter(item => {
      if (item.children) {
        const filteredChildren = item.children.filter(child => 
          this.hasPermissionForRoute(child.url, currentRole.permissions)
        );
        return filteredChildren.length > 0;
      }
      return this.hasPermissionForRoute(item.url, currentRole.permissions);
    }).map(item => {
      if (item.children) {
        return {
          ...item,
          children: item.children.filter(child => 
            this.hasPermissionForRoute(child.url, currentRole.permissions)
          )
        };
      }
      return item;
    });
  }

  private hasPermissionForRoute(url: string | undefined, permissions: string[]): boolean {
    if (!url) return false;
    
    // Map routes to permissions
    const routePermissionMap: { [key: string]: string } = {
      '/dashboard': 'dashboard',
      '/attendance': 'dashboard',
      '/case-management': 'case-management',
      '/formio-builder': 'form-builder',
      '/formio-data': 'form-data',
      '/analytics': 'analytics',
      '/users': 'user-management'
    };

    const requiredPermission = routePermissionMap[url];
    return requiredPermission ? permissions.includes(requiredPermission) : false;
  }
}
